import { useCallback, useEffect, useState } from "react";
import type { ExternalLinkListItem } from "./externalLinks.api";
import type { ExternalLinkTarget } from "./CreateExternalLinkModal";

/**
 * State machine behind the four external-link dialogs.
 *
 * They form a small flow rather than four independent modals — Manage opens Create/Edit/Stats/Revoke,
 * and those return to Manage — so ownership of "which dialog, on which link" lives here instead of
 * being spread across the File Manager. `useFileManager` only calls `openCreate` / `openManage` and
 * renders `<ExternalLinksHost/>`.
 */

export type ExternalLinkDialog = "none" | "create" | "edit" | "manage" | "stats" | "revoke";

/**
 * Cross-instance mutation bus.
 *
 * The dialogs are hosted by SEVERAL independent `useExternalLinks()` instances at once — the File
 * Manager grid and the details-panel section are the two standing ones. A link created through one
 * host must refresh the other's list and the grid's badge counts WITHOUT a browser reload (QA item
 * 20), so every mutation notifies every live instance rather than only its own.
 */
const mutationSubscribers = new Set<() => void>();

function notifyLinksMutated(): void {
  mutationSubscribers.forEach((listener) => listener());
}

export type UseExternalLinksResult = {
  dialog: ExternalLinkDialog;
  target: ExternalLinkTarget | null;
  activeLink: ExternalLinkListItem | null;
  /** The links a Revoke confirmation is about — one for the row action, several for a multi-selection. */
  activeLinks: ExternalLinkListItem[];
  /** Bumped on every mutation; the Manage list refetches when it changes. */
  refreshToken: number;
  /** Bumped when links change so the grid can refresh its badge counts. */
  linksChangedToken: number;
  openCreate: (target: ExternalLinkTarget) => void;
  openManage: (target: ExternalLinkTarget) => void;
  openEdit: (link: ExternalLinkListItem) => void;
  openStats: (link: ExternalLinkListItem) => void;
  openRevoke: (links: ExternalLinkListItem[]) => void;
  closeAll: () => void;
  /** Called by a dialog after a successful mutation. */
  handleMutated: () => void;
  /** Return to Manage from a child dialog (Edit / Stats / Revoke). */
  backToManage: () => void;
};

export function useExternalLinks(): UseExternalLinksResult {
  const [dialog, setDialog] = useState<ExternalLinkDialog>("none");
  const [target, setTarget] = useState<ExternalLinkTarget | null>(null);
  const [activeLink, setActiveLink] = useState<ExternalLinkListItem | null>(null);
  const [activeLinks, setActiveLinks] = useState<ExternalLinkListItem[]>([]);
  const [refreshToken, setRefreshToken] = useState(0);
  const [linksChangedToken, setLinksChangedToken] = useState(0);

  const openCreate = useCallback((next: ExternalLinkTarget) => {
    setTarget(next);
    setActiveLink(null);
    setDialog("create");
  }, []);

  const openManage = useCallback((next: ExternalLinkTarget) => {
    setTarget(next);
    setActiveLink(null);
    setDialog("manage");
  }, []);

  const openEdit = useCallback((link: ExternalLinkListItem) => {
    setActiveLink(link);
    setDialog("edit");
  }, []);

  const openStats = useCallback((link: ExternalLinkListItem) => {
    setActiveLink(link);
    setDialog("stats");
  }, []);

  const openRevoke = useCallback((links: ExternalLinkListItem[]) => {
    setActiveLinks(links);
    setDialog("revoke");
  }, []);

  const closeAll = useCallback(() => {
    setDialog("none");
    setTarget(null);
    setActiveLink(null);
    setActiveLinks([]);
  }, []);

  const backToManage = useCallback(() => {
    setActiveLink(null);
    setActiveLinks([]);
    setDialog("manage");
  }, []);

  // Every instance bumps its own counters when ANY instance mutates — see the module-level bus.
  useEffect(() => {
    const listener = () => {
      setRefreshToken((v) => v + 1);
      setLinksChangedToken((v) => v + 1);
    };
    mutationSubscribers.add(listener);
    return () => {
      mutationSubscribers.delete(listener);
    };
  }, []);

  /**
   * Broadcast rather than set locally: the instance that hosted the dialog is itself a subscriber,
   * so it refreshes through the same path as every other live surface (details panel, grid badges).
   */
  const handleMutated = useCallback(() => {
    notifyLinksMutated();
  }, []);

  return {
    dialog,
    target,
    activeLink,
    activeLinks,
    refreshToken,
    linksChangedToken,
    openCreate,
    openManage,
    openEdit,
    openStats,
    openRevoke,
    closeAll,
    handleMutated,
    backToManage,
  };
}
